// src/admin/layouts/AdminSidebar.jsx
import React, { useState } from 'react';
import classNames from 'classnames';
import { NavLink, useLocation } from 'react-router-dom';
import { navItems, orgSettingsItems, ORG_SETTINGS_PATH_PREFIX } from './navConfig';
import { getReleasesLastOpened } from '../pages/ReleasesPage';
import { useCurrentUser } from '../auth/CurrentUserContext';
import { useAdminAuth } from '../auth/AdminAuthContext';
import MarkerPill from '../../shared/components/pill/MarkerPill';
import UserAvatar from './users/UserAvatar';
import AdminModal from '../components/common/AdminModal';
import FormInput from '../../shared/components/FormInput';
import PersonNameDisplay from './common/PersonNameDisplay';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { can } from '../auth/permissions';
import useIsNarrowViewport from '../../shared/hooks/useIsNarrowViewport';
import { API_URL, publicPath } from '../../config/env';

const logoUrl = publicPath('/images/defti_logo.svg');
const logoUrlCollapsed = publicPath('/images/maintella_logo_m.svg');

export default function AdminSidebar({ collapsed = false }) {
  const { user, updateCurrentUser } = useCurrentUser();
  const { logout } = useAdminAuth();

  // Responsive collapse for small viewports. The same definition of "narrow" the lists and the
  // nav-group landing page use, so the whole admin UI changes shape at one width.
  const isSmallViewport = useIsNarrowViewport();

  const visibleNavItems = navItems.filter(item => !item.requiredAction || can(user, item.requiredAction));
  const visibleOrgSettingsItems = orgSettingsItems.filter(
    item => !item.requiredAction || can(user, item.requiredAction)
  );

  const [orgSettingsOpen, setOrgSettingsOpen] = useState(true);
  const { pathname } = useLocation();
  const onAnyOrgSettingsPage = pathname.startsWith(ORG_SETTINGS_PATH_PREFIX);

  // Two or more children is a choice worth a page; one is not.
  const hasOrgSettingsChoice = visibleOrgSettingsItems.length > 1;
  const orgSettingsTarget = hasOrgSettingsChoice
    ? ORG_SETTINGS_PATH_PREFIX
    : visibleOrgSettingsItems[0]?.to;

  // Determine if sidebar should be collapsed (prop or viewport)
  const isCollapsed = collapsed || isSmallViewport;

  // Modal state and form state
  const [showModal, setShowModal] = useState(false);
  const [givenName, setGivenName] = useState(user?.given_name || '');
  const [familyName, setFamilyName] = useState(user?.family_name || '');

  // Validation: both fields must be filled
  const canSave = givenName.trim() && familyName.trim();

  // For the collapsed menu's tooltip only. PersonNameDisplay renders the name in the menu
  // itself; a `title` has to be a plain string, and falls back to the email when there is no
  // name yet.
  const displayName = [user?.given_name, user?.family_name].filter(Boolean).join(' ')
    || user?.email
    || '';

  // Reset form when opening modal
  const handleOpenModal = () => {
    setGivenName(user?.given_name || '');
    setFamilyName(user?.family_name || '');
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  const handleSave = async () => {
    // PATCH request to update user details
    try {
      const res = await fetchWithAuth(
        `${API_URL}/v1/admin/users/me/edit`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user: {
              given_name: givenName,
              family_name: familyName,
            },
          }),
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.errors?.join(', ') || res.statusText || 'Update failed');
      }
      // Update local user state so sidebar reflects changes
      const updatedUser = await res.json();
      updateCurrentUser({ ...user, ...updatedUser });
      setShowModal(false);
    } catch (err) {
      alert(err.message || 'Failed to update user details');
    }
  };

  // Unread indicator for Releases
  const [hasUnreadReleases, setHasUnreadReleases] = useState(false);

  React.useEffect(() => {
    // Fire-and-forget check: fetch channel latest and compare to last opened
    const check = async () => {
      try {
        const res = await fetchWithAuth(`${API_URL}/v1/admin/feeds/releases`);
        if (!res.ok) return;
        const json = await res.json();
        const items = Array.isArray(json?.items) ? json.items : [];
        const latest = items
          .map(i => new Date(i.pubDate))
          .filter(d => !isNaN(d.getTime()))
          .sort((a, b) => b - a)[0];
        const lastOpenedIso = getReleasesLastOpened();
        if (latest && lastOpenedIso) {
          setHasUnreadReleases(latest > new Date(lastOpenedIso));
        } else if (latest && !lastOpenedIso) {
          setHasUnreadReleases(true);
        }
      } catch {
        // ignore
      }
    };
    check();
  }, []);

  // The rail itself does not scroll: `overflow-y: auto` on it also clipped horizontally (CSS
  // coerces the other axis away from `visible`), which would cut off the user menu's dropup
  // inside a 4rem column. The nav list scrolls instead, so the brand and the user menu stay put
  // and only the links move.
  return (
    <aside
      className={classNames(
        'd-flex flex-column flex-shrink-0 border-end sidebar-min-vh-100',
        { 'p-3': !isCollapsed, 'sidebar-collapsed': isCollapsed }
      )}
      style={{
        width: isCollapsed ? '4rem' : '15rem',
        position: 'sticky',
        top: 0,
        height: '100vh',
        overflow: 'visible'
      }}
    >
      {/* brand ---------------------------------------------------- */}
      <div className="sidebar-header mb-2 ms-1 mt-1 text-center">
        <img
          src={isCollapsed ? logoUrlCollapsed : logoUrl}
          alt="Defti Logo"
          style={{ maxWidth: '100%', height: '2rem', objectFit: 'contain' }}
          className="mb-2 mt-1"
        />
      </div>

      {/* nav ------------------------------------------------------ */}
      {/* `flex-nowrap` is load-bearing. Bootstrap's `.nav` sets `flex-wrap: wrap`, which did
          nothing while this list could grow as tall as it liked — but it is a height-constrained
          flex child now, and a short viewport made the items wrap into a *second column* beside
          the first, widening the rail past its own width and producing a horizontal scrollbar.
          A list of nav links has one column; when it does not fit, it scrolls down.
          `overflow-x: hidden` so the axis CSS would otherwise coerce to `auto` cannot put the
          bar back. */}
      <ul
        aria-label="Admin sections"
        className="nav nav-pills flex-column flex-nowrap gap-1 mb-auto"
        style={{ minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}
      >
        {visibleNavItems.map(({ to, icon, label }) => (
          <li className="nav-item" key={to}>
            {/* Collapsed, the label is visually hidden and this title is the only thing a
                pointer user has to go on. */}
            <NavLink
              to={to}
              title={label}
              className={({ isActive }) =>
                classNames(
                  'nav-link d-flex align-items-center gap-2',
                  isActive ? 'active' : 'link-dark'
                )
              }
            >
              <i className={`bi ${icon} fs-5`} />
              <span className="fw-semibold d-flex align-items-center gap-1">
                {label}
                {to === '/admin/releases' && hasUnreadReleases && (
                  <MarkerPill className="ms-1">new</MarkerPill>
                )}
              </span>
            </NavLink>
          </li>
        ))}

        {can(user, 'organization.settings') && visibleOrgSettingsItems.length > 0 &&
          (isCollapsed ? (
            /* Collapsed, the group used to flatten into one rail item per settings page --
               seven of them under the eight main items, which is what pushed the rail past the
               viewport and gave it a scrollbar.

               One item now stands for the group. It does not open the first page: that lands you
               on Forms when you meant API tokens, with nothing to say the others exist. It opens
               the group's own page instead, one card per child. With a single child there is
               nothing to choose, so the rail links straight to it and skips the page -- which is
               also what that page does if it is reached directly. */
            <li className="nav-item" key="organization-settings-collapsed">
              <NavLink
                to={orgSettingsTarget}
                title={
                  hasOrgSettingsChoice
                    ? 'Organization settings'
                    : `Organization settings — ${visibleOrgSettingsItems[0].label}`
                }
                className={classNames(
                  'nav-link d-flex align-items-center gap-2',
                  onAnyOrgSettingsPage ? 'active' : 'link-dark'
                )}
              >
                <i className="bi bi-gear fs-5" />
                <span className="fw-semibold">Org settings</span>
              </NavLink>
            </li>
          ) : (
            <li className="nav-item w-100" key="organization-settings-group">
              <button
                type="button"
                className={classNames(
                  'nav-link d-flex align-items-center gap-2 w-100 btn btn-link text-start text-decoration-none',
                  'link-dark'
                )}
                onClick={() => setOrgSettingsOpen(o => !o)}
                aria-expanded={orgSettingsOpen}
              >
                <i className="bi bi-gear fs-5" />
                <span className="fw-semibold flex-grow-1">Organization settings</span>
                <i
                  className={classNames('bi fs-6', orgSettingsOpen ? 'bi-chevron-up' : 'bi-chevron-down')}
                />
              </button>
              {orgSettingsOpen && (
                <ul className="nav nav-pills flex-column gap-1 ms-3 ps-2 border-start mt-1 mb-0">
                  {visibleOrgSettingsItems.map(({ to, label, icon }) => (
                    <li className="nav-item" key={to}>
                      <NavLink
                        to={to}
                        title={`Organization settings — ${label}`}
                        className={({ isActive }) =>
                          classNames(
                            'nav-link d-flex align-items-center gap-2 py-1',
                            isActive ? 'active' : 'link-dark'
                          )
                        }
                      >
                        <i className={`bi ${icon} fs-6`} />
                        <span className="small fw-semibold">{label}</span>
                      </NavLink>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
      </ul>

      {/* user card at the bottom --------------------------------- */}
      {isCollapsed ? (
        /* Collapsed, the name-and-Logout card does not fit in 4rem, so it used to be hidden
           outright — which left no way to log out or edit your details at all without expanding
           the sidebar first. The avatar becomes the trigger for a dropup carrying both, the same
           shape as the menu on the field engineer's bottom bar. */
        <div className="dropup mt-auto d-flex justify-content-center p-2">
          <button
            type="button"
            className="btn btn-link p-0 border-0"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            title={displayName ? `${displayName} — account menu` : 'Account menu'}
            aria-label="Account menu"
          >
            <UserAvatar user={user} size={32} />
          </button>
          <ul className="dropdown-menu shadow-sm">
            <li>
              <button
                type="button"
                className="dropdown-item d-flex align-items-center"
                onClick={handleOpenModal}
              >
                <i className="bi bi-person me-2" />
                <PersonNameDisplay
                  givenName={user?.given_name}
                  familyName={user?.family_name}
                  email={user?.email}
                  variant="inline"
                />
              </button>
            </li>
            <li>
              <button
                type="button"
                className="dropdown-item d-flex align-items-center"
                onClick={logout}
              >
                <i className="bi bi-box-arrow-right me-2" />
                Logout
              </button>
            </li>
          </ul>
        </div>
      ) : (
        <div className="sidebar-user mt-auto small d-flex align-items-center gap-2 p-2">
          <UserAvatar user={user} size={40} className="me-2" />
          <div>
            <div
              className="fw-semibold cursor-pointer"
              style={{ cursor: 'pointer' }}
              onClick={handleOpenModal}
              role="button"
              tabIndex={0}
            >
              <PersonNameDisplay
                givenName={user?.given_name}
                familyName={user?.family_name}
                email={user?.email}
                variant="inline"
              />
            </div>
            <div>
              <button
                className="btn btn-link btn-sm p-0 text-muted"
                style={{ textDecoration: 'none' }}
                onClick={logout}
                type="button"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit user modal ----------------------------------------- */}
      <AdminModal
        show={showModal}
        onClose={handleCloseModal}
        title="Edit your details"
        footer={
          <div className="d-flex justify-content-end gap-2">
            <button className="btn btn-secondary" onClick={handleCloseModal} type="button">
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              type="button"
              disabled={!canSave}
            >
              Save
            </button>
          </div>
        }
      >
        <FormInput
          id="givenName"
          label="Given name"
          value={givenName}
          onChange={e => setGivenName(e.target.value)}
          required
        />
        <FormInput
          id="familyName"
          label="Family name"
          value={familyName}
          onChange={e => setFamilyName(e.target.value)}
          required
        />
      </AdminModal>
    </aside>
  );
}
